import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import InputBox from 'src/components/Common/InputBox/InputBox';
import * as Yup from 'yup';
import { companyName, validation } from 'src/constants/general';
import { toaster } from 'src/utils/toaster';
import { adminUpdate, createAdmin } from 'src/services/admin';
import ResponsiveTextWithIconButton from 'src/components/Responsive/ResponsiveTextWithIconButton';
import { Dropdown } from 'primereact/dropdown';
import rightIcon from 'src/assets/svgs/rightIcon.svg';
import { roleList } from 'src/services/roles';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import IconWithButton from 'src/components/Common/IconWithButton';
import westImg from 'src/assets/img/westImg.png';
import logImg from 'src/assets/img/logImg.png';
import fsImg from 'src/assets/img/fsImg.png';
import lianiImg from 'src/assets/img/lianiImg.png';

const parseCompanyIds = (companyIds: any) => {
    if (Array.isArray(companyIds)) return companyIds.map(Number);
    if (companyIds === null || companyIds === undefined || companyIds === '') return [];

    try {
        const parsed = JSON.parse(companyIds);
        return Array.isArray(parsed) ? parsed.map(Number) : [Number(parsed)];
    } catch {
        return String(companyIds)
            .split(',')
            .map(Number)
            .filter((id) => !Number.isNaN(id));
    }
};

const AddNewAdmin = ({ setIsSave, isSave, isOnSuccess, setBtnDisabled, btnDisabled, isUpdate, selectedRowData }: any) => {
    const [error, setError] = useState('');
    const [isSaveValidation, setIsSaveValidation] = useState(false);
    const [roleListData, setRoleListData] = useState([]);
    const [roleOptions, setRoleOptions] = useState<any>([]);
    const allListData = useSelector((state: RootState) => state.getCommonApi);

    useEffect(() => {
        if (isSave) {
            formik.handleSubmit();
            setIsSave(false);
            setIsSaveValidation(true);
        }
    }, [isSave]);
    const getInitialValues = useCallback(() => {
        if (isUpdate && selectedRowData) {
            return {
                name: selectedRowData?.adminFullName.split(' ')[0],
                email: selectedRowData?.adminEmail,
                surName: selectedRowData?.adminFullName.split(' ')[1],
                password: '',
                passwordConfirmation: '',
                type: selectedRowData?.adminType,
                roleId: selectedRowData?.adminRoleId,
                roleName: selectedRowData?.adminRoleName,
                companyIds: parseCompanyIds(selectedRowData?.companyIds ?? selectedRowData?.companyId),
            };
        }

        return {
            name: '',
            email: '',
            surName: '',
            password: '',
            passwordConfirmation: '',
            type: 'Admin',
            roleId: roleOptions.find((item: any) => item.name === 'admin')?.value || 2,
            roleName: 'admin',
            companyIds: [],
        };
    }, [isUpdate, selectedRowData]);
    // const roles = [
    //     { label: 'Admin', value: 'Admin' },
    //     { label: 'Accountant', value: 'Accountant' },
    // ];

    const getRoleList = async () => {
        try {
            let url = '?page=0&limit=50';
            const roleResData = await roleList(url);
            const data = roleResData?.data?.data;
            setRoleListData(data.roles);
        } catch (err: any) {}
    };

    useEffect(() => {
        getRoleList();
    }, []);
    useEffect(() => {
        if (roleListData.length !== 0) {
            const roleOption = roleListData.map((item: any) => ({
                label: item.display_name,
                value: item.id,
                name: item.name,
            }));
            setRoleOptions(roleOption);
        }
    }, [roleListData]);

    const onSubmit = async (values: any, helpers: any) => {
        setBtnDisabled(true);
        const payload = {
            name: `${values.name} ${values.surName}`,
            email: values.email,
            password: values.password,
            type: values.type,
            roleId: values.roleId,
            roleName: values.roleName,
            companyIds: values.companyIds,
        };
        try {
            if (isUpdate) {
                const updatePayload = { ...payload, userId: selectedRowData.adminId };
                const res = await adminUpdate(updatePayload);
                if (res.status === 200) {
                    isOnSuccess();
                    toaster('success', res.data.message);
                } else {
                    setError(res.data.message);
                    setBtnDisabled(false);
                }
            } else {
                const res = await createAdmin(payload);
                if (res.status === 200) {
                    isOnSuccess();
                    toaster('success', res.data.message);
                } else {
                    setError(res.data.message);
                    setBtnDisabled(false);
                }
            }
        } catch (err: any) {
            setError(err?.response.message);
        }
    };

    const formik = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        validationSchema: Yup.object({
            name: Yup.string().required('Please enter name'),
            email: Yup.string().required('Please enter email').email('Please enter proper email'),
            surName: Yup.string().required('Please enter surname'),
            ...(!isUpdate && {
                password: Yup.string().required('Please enter password').min(6, 'Password required minimum 6 characters'),

                passwordConfirmation: Yup.string()
                    .required('Please enter confirm password')
                    .oneOf([Yup.ref('password'), ''], 'Passwords must match'),
            }),
            type: Yup.string().required('Please enter type'),
            companyIds: Yup.array().of(Yup.number()).min(1, 'Please select at least one company').required('Please select at least one company'),
        }),
        onSubmit: onSubmit,
    });

    return (
        <>
            <div className="p-8 mt-6 bg-white rounded-3xl">
                <div>
                    <div>
                        <p className="text-black text-base font-black">Adding new Admin</p>
                        <p className="text-black text-sm font-medium leading-none mt-2">all the information bellow is required to add new admin</p>
                    </div>
                    <form className="mt-4 gap-5">
                        <div className="flex flex-row gap-5 max-lg:flex-col">
                            <div className="flex flex-col max-lg:flex-row gap-5">
                                <div className="flex flex-col md:flex-row gap-5 max-lg:w-full">
                                    <div className="md:w-1/2 lg:w-[230px] w-full">
                                        <InputBox
                                            name={'name'}
                                            type={'text'}
                                            className="bg-gray10 border-none"
                                            onChange={(e: any) => {
                                                formik.handleChange(e);
                                                setError('');
                                                setIsSaveValidation(false);
                                            }}
                                            // onBlur={formik.handleBlur}
                                            inputId={'floating_filled_admin_1'}
                                            value={formik.values.name}
                                            placeholder="Enter First Name"
                                            error={formik.touched.name && isSaveValidation ? (formik.errors.name as string) : ''}
                                            maxLength={validation.name}
                                        />
                                    </div>
                                    <div className="md:w-1/2 lg:w-[230px] w-full">
                                        <InputBox
                                            name={'surName'}
                                            type={'text'}
                                            className="bg-gray10 border-none"
                                            onChange={(e: any) => {
                                                formik.handleChange(e);
                                                setError('');
                                                setIsSaveValidation(false);
                                            }}
                                            // onBlur={formik.handleBlur}
                                            inputId={'floating_filled_admin_4'}
                                            value={formik.values.surName}
                                            placeholder="Enter Last Name"
                                            error={formik.touched.surName && isSaveValidation ? (formik.errors.surName as string) : ''}
                                            maxLength={validation.surname}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row gap-5">
                                <div className=" max-lg:w-full lg:w-[230px]">
                                    <InputBox
                                        name={'email'}
                                        type={'email'}
                                        className="bg-gray10 border-none"
                                        onChange={(e: any) => {
                                            formik.handleChange(e);
                                            setError('');
                                            setIsSaveValidation(false);
                                        }}
                                        // onBlur={formik.handleBlur}
                                        inputId={'floating_filled_admin_2'}
                                        value={formik.values.email}
                                        placeholder="Enter Email"
                                        error={formik.touched.email && isSaveValidation ? (formik.errors.email as string) : ''}
                                        maxLength={validation.email}
                                    />
                                </div>
                            </div>
                            {isUpdate && (
                                <div className=" md:w-1/2 lg:w-[230px]  w-full ">
                                    <Dropdown
                                        value={formik.values.roleId}
                                        options={roleOptions}
                                        optionLabel="label"
                                        // optionValue="value"
                                        placeholder="Role"
                                        className="w-full border"
                                        onChange={(e) => {
                                            const selectedRole = roleOptions.find((item: any) => item.value === e.value);
                                            formik.setFieldValue('type', selectedRole.label);
                                            formik.setFieldValue('roleId', selectedRole.value);
                                            formik.setFieldValue('roleName', selectedRole.name);

                                            setError('');
                                        }}
                                        onBlur={() => {
                                            formik.setFieldTouched('type', true);
                                            formik.setFieldTouched('roleId', true);
                                            formik.setFieldTouched('roleName', true);
                                        }}
                                    />
                                    {formik.touched.type && formik.errors.type && <p className="text-red-500 text-xs">{String(formik.errors.type)}</p>}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col md:flex-row gap-5 mt-5 ">
                            {!isUpdate && (
                                <>
                                    <div className="  md:w-1/2 lg:w-[230px] w-full">
                                        <InputBox
                                            name={'password'}
                                            type={'password'}
                                            className="bg-gray10 border-none"
                                            onChange={(e: any) => {
                                                formik.handleChange(e);
                                                setError('');
                                                setIsSaveValidation(false);
                                            }}
                                            // onBlur={formik.handleBlur}
                                            inputId={'floating_filled_admin_5'}
                                            value={formik.values.password}
                                            placeholder="Enter Password"
                                            error={formik.errors.password && formik.touched.password && isSaveValidation ? formik.errors.password : ''}
                                            maxLength={validation.password}
                                        />
                                    </div>
                                    <div className=" md:w-1/2 lg:w-[230px]  w-full">
                                        <InputBox
                                            name={'passwordConfirmation'}
                                            type={'password'}
                                            className="bg-gray10 border-none"
                                            onChange={(e: any) => {
                                                formik.handleChange(e);
                                                setError('');
                                                setIsSaveValidation(false);
                                            }}
                                            // onBlur={formik.handleBlur}
                                            inputId={'floating_filled_admin_3'}
                                            value={formik.values.passwordConfirmation}
                                            placeholder="Enter  Confirm Password"
                                            error={formik.errors.passwordConfirmation && formik.touched.passwordConfirmation && isSaveValidation ? formik.errors.passwordConfirmation : ''}
                                            maxLength={validation.email}
                                        />
                                    </div>

                                    <div className=" md:w-1/2 lg:w-[230px]  w-full ">
                                        <Dropdown
                                            value={formik.values.roleId}
                                            options={roleOptions}
                                            optionLabel="label"
                                            // optionValue="value"
                                            placeholder="Role"
                                            className="w-full border"
                                            onChange={(e) => {
                                                const selectedRole = roleOptions.find((item: any) => item.value === e.value);
                                                formik.setFieldValue('type', selectedRole.label);
                                                formik.setFieldValue('roleId', selectedRole.value);
                                                formik.setFieldValue('roleName', selectedRole.name);
                                                setError('');
                                            }}
                                            onBlur={() => {
                                                formik.setFieldTouched('type', true);
                                                formik.setFieldTouched('roleId', true);
                                                formik.setFieldTouched('roleName', true);
                                            }}
                                        />
                                        {formik.touched.type && formik.errors.type && <p className="text-red-500 text-xs">{String(formik.errors.type)}</p>}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="mt-5">
                            <div className="flex gap-2 md:justify-start items-center">
                                {allListData.companyData.map((data: any, index: number) => (
                                    <div className="" key={index}>
                                        <IconWithButton
                                            title={data?.name}
                                            icon={data?.name === companyName.West ? westImg : data?.name === companyName.Log ? logImg : data?.name === companyName.Liani ? lianiImg : fsImg}
                                            isColor={formik.values.companyIds.includes(Number(data?.id))}
                                            onclick={() => {
                                                const companyId = Number(data?.id);

                                                const companyIds = formik.values.companyIds.includes(companyId)
                                                    ? formik.values.companyIds.filter((id: number) => id !== companyId)
                                                    : [...formik.values.companyIds, companyId];

                                                formik.setFieldValue('companyIds', companyIds);
                                                setError('');
                                                setIsSaveValidation(false);
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                            {formik.errors.companyIds && isSaveValidation && <p className="text-red-500 text-xs mt-1">{String(formik.errors.companyIds)}</p>}
                        </div>
                        {error && <p className="text-red-600 mt-10 text-sm text-center">{error}</p>}
                    </form>
                </div>
            </div>
            <div className="hidden max-lg:block  pb-3 max-md:pt-5 max-lg:pt-[26px]">
                <ResponsiveTextWithIconButton title={'SAVE'} icon={rightIcon} isDisabled={btnDisabled} onclick={() => setIsSave(true)} />
            </div>
        </>
    );
};
export default AddNewAdmin;
